import { ApiError } from "@client/lib/api";

/**
 * One sentence a person can act on, from whatever was thrown.
 *
 * The server writes the message for anything it recognises — it knows which
 * email, which scenario, which currency — so the job here is mostly to not get
 * in the way of that, and to say something honest about the cases where no
 * server was reached at all.
 */
export function describeError(error: unknown, fallback = "Something went wrong."): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "MALFORMED_RESPONSE":
        return "The server sent something this page could not read. Reload and try again.";
      case "NOT_AUTHENTICATED":
        return "Your session has ended. Sign in again to continue.";
      case "CSRF_FAILED":
        return "That request looked like it came from another site and was refused. Reload the page.";
      case "INTERNAL_ERROR":
        return "The server hit an error handling that. Try again; if it keeps happening, tell an administrator.";
      default:
        return error.message || fallback;
    }
  }
  if (error instanceof Error && error.message === "Failed to fetch") {
    return "Could not reach the server. Check your connection and try again.";
  }
  return fallback;
}

/**
 * Field-level messages from a `400 VALIDATION_FAILED`, keyed by field name, so a
 * form can put the server's complaint against the input that caused it instead
 * of dumping a paragraph above the whole thing.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const details = error.details as
    { field?: string; issues?: { path?: (string | number)[]; message?: string }[] } | undefined;
  if (!details) return {};

  const out: Record<string, string> = {};
  if (typeof details.field === "string") out[details.field] = error.message;
  for (const issue of details.issues ?? []) {
    const field = issue.path?.[0];
    if (typeof field === "string" && issue.message && !out[field]) out[field] = issue.message;
  }
  return out;
}
